/**
 * Module 67 (WP-67.B1) — the Gate Widener power-up's rule, pure.
 *
 * Widening a gate is exactly one thing: its `span` growing by one cell along
 * the wall. `exitsThroughGate` reads span only through `gateLanes`, and the
 * solver compiles exits fresh into lanes on every call, so nothing else in the
 * movement model changes — the whole correctness burden is picking WHERE the
 * extra lane goes, and refusing when nowhere is legal.
 *
 * The generator maintains two invariants a runtime widen must not break
 * (`free-generator.ts`, gate placement): a gate never leaves the board
 * (`offset ∈ [0, gridSize - span]`) and gates on the same wall never share
 * lanes (`takenLanes`). The growth rule is therefore:
 *
 *   1. try the HIGHER lane (`start + span`): in bounds and not occupied by any
 *      other gate on the same wall;
 *   2. else try the LOWER lane (`start - 1`), shifting the anchor `position`
 *      down to it (the anchor is the first lane by definition);
 *   3. else the gate cannot widen — and a gate that cannot widen is NOT a
 *      valid target for the power-up (the arm step filters on this, so the
 *      player is never sold a no-op).
 *
 * Deterministic on purpose: the same board always widens the same way, so a
 * replayed level, an undo trace and a test all agree.
 *
 * "Same wall" is the exit's `direction` — the four walls are the four
 * directions, and every gate's fixed (travel) coordinate on a wall is shared,
 * so lanes are comparable exactly between same-direction exits.
 */
/**
 * A gate's identity for targeting. Exits carry no id; direction + anchor cell
 * is unique (same-wall gates never share lanes, so anchors differ). Note the
 * key CHANGES when a widen shifts the anchor to a lower lane — callers hold
 * keys only for the duration of one targeting interaction, never across moves.
 */
export function exitKey(exit) {
    return `${exit.direction}:${exit.position.row},${exit.position.col}`;
}
const isHorizontal = (direction) => direction === 'left' || direction === 'right';
/** The lanes (rows for a left/right gate, cols for up/down) a gate occupies. */
function laneRange(exit) {
    return {
        start: isHorizontal(exit.direction) ? exit.position.row : exit.position.col,
        span: exit.span ?? 1,
    };
}
/**
 * The exit as it would be after widening one cell — or `null` when no legal
 * growth side exists. Pure: never mutates its inputs.
 */
export function widenedExit(exit, allExits, gridSize) {
    const { start, span } = laneRange(exit);
    if (span >= gridSize)
        return null;
    const key = exitKey(exit);
    const taken = new Set();
    for (const other of allExits) {
        if (other.direction !== exit.direction || exitKey(other) === key)
            continue;
        const r = laneRange(other);
        for (let i = 0; i < r.span; i += 1)
            taken.add(r.start + i);
    }
    const higher = start + span;
    if (higher < gridSize && !taken.has(higher))
        return { ...exit, span: span + 1 };
    const lower = start - 1;
    if (lower >= 0 && !taken.has(lower)) {
        const position = isHorizontal(exit.direction)
            ? { row: lower, col: exit.position.col }
            : { row: exit.position.row, col: lower };
        return { ...exit, position, span: span + 1 };
    }
    return null;
}
/** Whether the power-up may be aimed at this gate at all. */
export function canWidenGate(exit, allExits, gridSize) {
    return widenedExit(exit, allExits, gridSize) !== null;
}
/** Every gate on the board the power-up may currently be aimed at, as keys. */
export function widenableGateKeys(state) {
    const { exits, gridSize } = state.puzzle;
    return exits.filter((e) => canWidenGate(e, exits, gridSize)).map(exitKey);
}
/**
 * The board with the gate at `key` widened — or `null` when the key names no
 * gate or that gate cannot widen (callers treat null as a refusal, exactly
 * like an effect whose `targets()` came back empty).
 *
 * A NEW state object with a new `puzzle.exits` array; the exit itself is
 * replaced, not mutated, because the renderer holds `puzzle.exits` by
 * reference and a silent in-place mutation would repaint at an unpredictable
 * later frame instead of on the `setBoard` that follows this call.
 */
export function widenGate(state, key) {
    const { exits, gridSize } = state.puzzle;
    const index = exits.findIndex((e) => exitKey(e) === key);
    if (index < 0)
        return null;
    const widened = widenedExit(exits[index], exits, gridSize);
    if (!widened)
        return null;
    const nextExits = exits.slice();
    nextExits[index] = widened;
    return { ...state, puzzle: { ...state.puzzle, exits: nextExits } };
}
